import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "./useAuth";
import { showError } from "../constants/toasts";

// Permission interface based on your sub_role.permissions structure
interface Permission {
  id: number;
  module: string;
  can_add_edit: boolean;
  can_view: boolean;
  can_be_delete: boolean;
}

// Available modules based on your permissions structure
type ModuleName =
  | "Dashboard"
  | "Notification Management"
  | "Manage Customer Support"
  | "Settings"
  | "Manage Customers"
  | "Manage Analytics"
  | "Manage CMS"
  | "Manage Subscriptions"
  | "Manage SubAdmin"
  | "Budget Intelligence"
  | "Document Processing"
  | "Hydration And Goal"
  | "Label Recognition"
  | "Location Intelligence"
  | "Manage Revenue"
  | "Shared Account"
  | "Shopping Meals"
  | "Translation Messaging"
  | "Reminder Engine"
  | "AI Behavior Language";

// Permission types that can be checked
type PermissionType = "can_view" | "can_add_edit" | "can_be_delete";
const usePermission = ({
  module,
  permission_type,
}: {
  module: ModuleName;
  permission_type: PermissionType;
}) => {
  const userData = useAuth();
  const navigate = useNavigate();
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Create canonical strings for robust, case and punctuation-insensitive matching
  const canonicalize = (value: string | undefined | null) =>
    (value || "")
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/\//g, " ")
      .replace(/\s+/g, " ")
      .replace(/[^a-z0-9]+/g, "")
      .trim();

  // Map UI module labels to backend labels when they differ
  const backendAliasByUiCanonical: Record<string, string[]> = useMemo(
    () => ({
      // Exact or near-exact matches still included for clarity
      [canonicalize("Dashboard")]: [canonicalize("Dashboard")],
      [canonicalize("Settings")]: [canonicalize("Settings")],
      [canonicalize("Manage Customers")]: [
        canonicalize("User Management"),
        canonicalize("Manage Users"),
      ],
      [canonicalize("Manage Analytics")]: [canonicalize("Manage Analytics")],
      [canonicalize("Manage CMS")]: [canonicalize("Manage CMS Pages")],
      [canonicalize("Manage Subscriptions")]: [
        canonicalize("Subscription & Plan"),
      ],
      [canonicalize("Manage SubAdmin")]: [
        canonicalize("User Management"),
        canonicalize("Manage Subadmin"),
      ],
      [canonicalize("Budget Intelligence")]: [
        canonicalize("Budget Intelligence"),
      ],
      [canonicalize("Document Processing")]: [
        canonicalize("Document & OCR Processing"),
      ],
      [canonicalize("Hydration And Goal")]: [
        canonicalize("Health & Habit Configuration"),
      ],
      [canonicalize("Label Recognition")]: [canonicalize("Label Recognition")],
      [canonicalize("Location Intelligence")]: [
        canonicalize("Location Intelligence"),
      ],
      [canonicalize("Manage Revenue")]: [canonicalize("Manage Revenue")],
      [canonicalize("Shared Account")]: [
        canonicalize("Family / Shared Account"),
      ],
      [canonicalize("Shopping Meals")]: [canonicalize("Shopping & Meal AI")],
      [canonicalize("Translation Messaging")]: [
        canonicalize("Translation Messaging"),
      ],
      [canonicalize("Reminder Engine")]: [canonicalize("Reminder Engine")],
      [canonicalize("AI Behavior Language")]: [
        canonicalize("AI Behavior & Language"),
      ],
      [canonicalize("Notification Management")]: [
        canonicalize("Manage Notifications"),
        canonicalize("Notification Management"),
      ],
      [canonicalize("Manage Customer Support")]: [
        canonicalize("Manage Customer Support"),
      ],
    }),
    []
  );

  // Resolve which backend canonical module names should be matched for the given UI module
  const resolveBackendCanonicalTargets = (uiModule: ModuleName): string[] => {
    const key = canonicalize(uiModule);
    const aliases = backendAliasByUiCanonical[key];
    // Always include the UI canonical as a fallback to catch direct matches
    const set = new Set<string>([key, ...(aliases || [])]);
    return Array.from(set);
  };

  useEffect(() => {
    const checkPermission = () => {
      // If user is admin (role !== 2), grant all permissions
      if (userData?.role !== 2) {
        setHasPermission(true);
        setIsLoading(false);
        return;
      }

      // Check if user has sub_role and permissions
      if (!userData?.sub_role?.permissions || !Array.isArray(userData.sub_role.permissions)) {
        setHasPermission(false);
        setIsLoading(false);
        return;
      }

      // Find the permission for the specified module
      const targetCanonicals = resolveBackendCanonicalTargets(module);
      const modulePermission = userData.sub_role.permissions.find((perm: Permission) => {
        const permCanonical = canonicalize(perm.module);
        return targetCanonicals.includes(permCanonical);
      });

      if (!modulePermission) {
        // Module not found in permissions
        setHasPermission(false);
        setIsLoading(false);
        return;
      }

      // Check if user has the required permission type
      const permissionGranted = modulePermission[permission_type];
      setHasPermission(!!permissionGranted);
      setIsLoading(false);
    };

    checkPermission();
  }, [userData, module, permission_type]);

  // Redirect if permission is denied
  useEffect(() => {
    if (!isLoading && !hasPermission) {
      showError("Access denied");
      navigate("/permission-denied", { replace: true });
    }
  }, [hasPermission, isLoading, navigate]);

  // Helper functions to check specific permission types
  const canView = (moduleName: ModuleName) => {
    if (userData?.role !== 2) return true;
    const targets = resolveBackendCanonicalTargets(moduleName);
    const modulePermission = userData?.sub_role?.permissions?.find((perm: Permission) =>
      targets.includes(canonicalize(perm.module))
    );
    return !!modulePermission?.can_view;
  };

  const canAddEdit = (moduleName: ModuleName) => {
    if (userData?.role !== 2) return true;
    const targets = resolveBackendCanonicalTargets(moduleName);
    const modulePermission = userData?.sub_role?.permissions?.find((perm: Permission) =>
      targets.includes(canonicalize(perm.module))
    );
    return !!modulePermission?.can_add_edit;
  };

  const canDelete = (moduleName: ModuleName) => {
    if (userData?.role !== 2) return true;
    const targets = resolveBackendCanonicalTargets(moduleName);
    const modulePermission = userData?.sub_role?.permissions?.find((perm: Permission) =>
      targets.includes(canonicalize(perm.module))
    );
    return !!modulePermission?.can_be_delete;
  };

  // Get all permissions for a specific module
  const getModulePermissions = (moduleName: ModuleName) => {
    if (userData?.role !== 2) {
      return {
        can_view: true,
        can_add_edit: true,
        can_be_delete: true,
      };
    }

    const targets = resolveBackendCanonicalTargets(moduleName);
    const modulePermission = userData?.sub_role?.permissions?.find((perm: Permission) =>
      targets.includes(canonicalize(perm.module))
    );

    return {
      can_view: !!modulePermission?.can_view,
      can_add_edit: !!modulePermission?.can_add_edit,
      can_be_delete: !!modulePermission?.can_be_delete,
    };
  };

  return {
    // Main permission check for the specified module and permission type
    hasPermission,
    isLoading,
    
    // Helper functions for checking specific permissions
    canView,
    canAddEdit,
    canDelete,
    
    // Get all permissions for a module
    getModulePermissions,
    
    // Current user data
    userData,
    
    // Check if user is sub-admin
    isSubAdmin: userData?.role === 2,
  };
};

export default usePermission;
